import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { ShoppingCartOutlined } from "@material-ui/icons";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { STAFF_UPDATE } from "@saleor/config";
import { ContractQuery } from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface TableProductsProps {
  products: ContractQuery["contract"]["products"];
  onClickButton: (productId, contractMaterialId) => void;
  onAddToCart: (product) => void;
  staffUpdate?: number;
  cartData?: any;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      borderRadius: 6,
      border: "1px solid #E0E0E0",
      "& .MuiTableHead-root .MuiTableCell-root": {
        height: "48px !important"
      }
    },
    cellIndex: {
      maxWidth: 70,
      minWidth: 70,
      width: 70,
      lineHeight: 22,
      padding: "0 2.4rem !important"
    },
    btnOpen: {
      width: 146,
      height: 32
    },
    cellBtn: {
      maxWidth: 150,
      paddingRight: 0
    },
    colQuantity: {
      width: 120,
      minWidth: 120,
      maxWidth: 120
    },
    textError: {
      color: theme.palette.error.light,
      marginTop: 5,
      fontSize: 12
    }
  }),
  { name: "TableProductsDraw" }
);

const TableProducts: React.FC<TableProductsProps> = props => {
  const { products, onClickButton, onAddToCart, staffUpdate, cartData } = props;
  const [productsState, setProductsState] = useState([]);
  const intl = useIntl();
  const classes = useStyles();

  useEffect(() => {
    const listProductCustomer =
      products?.map(item => ({ ...item, quantity: "" })) || [];
    setProductsState(listProductCustomer);
  }, [products]);

  const handleChangeQuantity = (quantity, productId) => {
    const value = +quantity;

    const productChange = productsState.find(item => item.id === productId);

    const customerCartChange = cartData?.filter(
      item =>
        (item?.contractProductId || item?.contractProduct.id) === productId
    );

    productChange.quantity = value;

    if (customerCartChange) {
      let customerCartChangeQty = 0;
      customerCartChange.forEach(item => {
        customerCartChangeQty += item.quantity;
      });

      productChange.errorQty =
        productChange.remain / productChange.product.weight <
        value + customerCartChangeQty
          ? true
          : false;
    } else {
      productChange.errorQty =
        productChange.remain / productChange.product.weight < value
          ? true
          : false;
    }

    setProductsState(prevState => [...prevState]);
  };

  const getQuantityProduct = productId => {
    const product = productsState.find(item => item.id === productId);
    return product?.quantity || "";
  };

  const handleAddToCart = productId => {
    const product = productsState.find(item => item.id === productId);
    onAddToCart(product);
    handleChangeQuantity("", productId);
  };

  const isMatType8182 = product =>
    product.materialType === "81" || product.materialType === "82";

  const haveRowMatType8182 = () =>
    products?.some(product => isMatType8182(product.product));

  return (
    <div className={classes.root}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCellHeader className={classes.cellIndex} colSpan={2}>
              <MyTypography variant={"body3"}>
                {intl.formatMessage({ defaultMessage: "ลำดับ" })}
              </MyTypography>
            </TableCellHeader>
            <TableCellHeader>
              <MyTypography variant={"body3"}>
                {intl.formatMessage({ defaultMessage: "ช่ือสินค้า" })}
              </MyTypography>
            </TableCellHeader>
            <TableCellHeader className={classes.colQuantity}>
              <MyTypography variant={"body3"}>
                {haveRowMatType8182()
                  ? intl.formatMessage({ defaultMessage: "จำนวนสั่ง" })
                  : ""}
              </MyTypography>
            </TableCellHeader>
            <TableCellHeader colSpan={2}>
              <MyTypography variant={"body3"}>
                {haveRowMatType8182()
                  ? intl.formatMessage({ defaultMessage: "หน่วย" })
                  : ""}
              </MyTypography>
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {products &&
            renderCollection(products, (product, index) => (
              <TableRow key={product?.id}>
                <TableCell className={classes.cellIndex} colSpan={2}>
                  <MyTypography variant={"body3"}>{index + 1}</MyTypography>
                </TableCell>
                <TableCell>
                  <MyTypography variant={"subtitle3"}>
                    {product?.materialDescription}
                  </MyTypography>
                </TableCell>
                <TableCell>
                  {isMatType8182(product.product) ? (
                    <>
                      <NumberFormat
                        allowLeadingZeros
                        value={getQuantityProduct(product.id)}
                        error={productsState[index]?.errorQty || false}
                        onChange={e =>
                          handleChangeQuantity(e.target.value, product.id)
                        }
                      />
                      {productsState[index]?.errorQty && (
                        <div className={classes.textError}>
                          จำนวนที่สั่งมีมากกว่าจำนวนคงเหลือ
                        </div>
                      )}
                    </>
                  ) : (
                    ""
                  )}
                </TableCell>
                <TableCell>
                  {/* {isMatType8182(product.product)
                    ? intl.formatMessage({ defaultMessage: "ม้วน" })
                    : ""} */}
                  {isMatType8182(product.product) ? product.salesUnit : ""}
                </TableCell>
                <TableCell align={"center"} className={classes.cellBtn}>
                  {isMatType8182(product.product) ? (
                    <Button
                      id={`products[${index}].addToCartButton`}
                      color={"secondary"}
                      variant={"contained"}
                      className={classes.btnOpen}
                      onClick={() => handleAddToCart(product.id)}
                      startIcon={<ShoppingCartOutlined />}
                    >
                      <MyTypography variant="subtitle3" color="inherit">
                        {staffUpdate === STAFF_UPDATE.UPDATE_CART
                          ? "Add to cart"
                          : ""}
                        {staffUpdate === STAFF_UPDATE.UPDATE_ORDER
                          ? "Add to order"
                          : ""}
                      </MyTypography>
                    </Button>
                  ) : (
                    <Button
                      id={`products[${index}].selectProductButton`}
                      color={"secondary"}
                      variant={"contained"}
                      className={classes.btnOpen}
                      onClick={() =>
                        onClickButton(product?.product.id, product?.id)
                      }
                    >
                      <MyTypography variant="subtitle3" color="inherit">
                        เลือกสินค้า
                      </MyTypography>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default TableProducts;
